import React, {useCallback, useMemo} from 'react';
import {uniq} from 'lodash';
import {type AstView, type EnrichedAstNode} from 'rendition-core';

import {useCheckIfSomeSelectedNodeAbsolutePositioned} from 'Hooks/AstHooks/useCheckIfSomeSelectedNodeAbsolutePositioned';
import useChildAligmentToggles from 'Views/Preview/Builder/FlexEdit/useChildAligmentToggles';
import {ArrowClockwise, ArrowDown, ArrowRight, Check, Circle} from '@phosphor-icons/react';
import {EditorSectionTitle} from './EditingTitle';
import TabbedButtonToggle from 'Components/Tailwind/TabbedButtonToggle';
import {useViewContext} from 'Contexts/ViewContext';

interface Props {
    view: AstView;
    setView: (view: AstView) => void;
    selectedNodes: EnrichedAstNode[];
}

const useCheckFlexDirections = (view: AstView, selectedNodes: EnrichedAstNode[]) => {
    const directions = useMemo(
        () => selectedNodes.map((n) => n?.calculatedLayoutAbstraction!.flexDirection),
        [selectedNodes]
    );

    const allMatchingDirections = uniq(directions).length === 1;
    return {allMatchingDirections, direction: allMatchingDirections ? directions[0] : 'row'};
};

export const FlexControls = ({view, setView, selectedNodes}: Props) => {
    const {allMatchingDirections, direction} = useCheckFlexDirections(view, selectedNodes);
    const hasAbsPositionedNode = useCheckIfSomeSelectedNodeAbsolutePositioned(selectedNodes);
    const allHaveChildren = useMemo(() => selectedNodes.every((n) => n.children.length > 0), [selectedNodes]);

    if (!allMatchingDirections || hasAbsPositionedNode || !allHaveChildren) {
        return null;
    }

    return (
        <div className="flex flex-auto flex-col gap-2">
            <div className="flex w-full items-center justify-between">
                <EditorSectionTitle title="Alignment" />
                <DirectionSelector />
            </div>
            <GridSelector view={view} handleRepairGroup={setView} selectedNodes={selectedNodes} direction={direction} />
        </div>
    );
};

type GridPosition = {
    alignItems: 'flex-start' | 'center' | 'flex-end';
    justifyContent: 'flex-start' | 'center' | 'flex-end';
};

const gridPositions: GridPosition[][] = [
    [
        {alignItems: 'flex-start', justifyContent: 'flex-start'},
        {alignItems: 'flex-start', justifyContent: 'center'},
        {alignItems: 'flex-start', justifyContent: 'flex-end'},
    ],
    [
        {alignItems: 'center', justifyContent: 'flex-start'},
        {alignItems: 'center', justifyContent: 'center'},
        {alignItems: 'center', justifyContent: 'flex-end'},
    ],
    [
        {alignItems: 'flex-end', justifyContent: 'flex-start'},
        {alignItems: 'flex-end', justifyContent: 'center'},
        {alignItems: 'flex-end', justifyContent: 'flex-end'},
    ],
];

interface GridProps {
    view: AstView;
    selectedNodes: EnrichedAstNode[];
    handleRepairGroup: (view: AstView) => void;
    relativePosition?: boolean;
    direction: 'row' | 'column';
}

export const GridSelector: React.FC<React.PropsWithChildren<GridProps>> = ({
    view,
    selectedNodes,
    handleRepairGroup,
    direction,
}) => {
    const {updateSelectedNodes} = useViewContext();
    const {flexChildrenOfSelectedNodes, setJustifyContents, justifyContent, alignSelf} = useChildAligmentToggles({
        selectedNodes,
        view,
        handleRepairGroup,
    });

    const updateChildAlignments = useCallback(
        (alignment: GridPosition['alignItems']) => {
            updateSelectedNodes((node) => {
                node.children.forEach((child) => {
                    // base on parent only delete left right margin
                    if (direction === 'column') {
                        child.position.marginRight = 0;
                        child.position.marginLeft = 0;
                    } else {
                        child.position.marginTop = 0;
                        child.position.marginBottom = 0;
                    }
                    child.inputLayoutAbstraction.alignSelf = alignment;
                });
            });
        },
        [updateSelectedNodes, direction]
    );

    const isColumn = direction === 'column';
    // Get all alignSelf from the child of the node.
    const isMixedAlignSelf = useMemo(() => {
        return (
            uniq(flexChildrenOfSelectedNodes.map((child) => child.calculatedLayoutAbstraction!.alignSelf)).length > 1
        );
    }, [flexChildrenOfSelectedNodes]);
    const swapGridPosition = (position: GridPosition): GridPosition => {
        return {
            alignItems: position.justifyContent,
            justifyContent: position.alignItems,
        };
    };

    const handleGridItemClick = (position: GridPosition) => {
        setJustifyContents(position.justifyContent);
        updateChildAlignments(position.alignItems);
    };

    const getPosition = (): [number, number] => {
        for (let rowIndex = 0; rowIndex < gridPositions.length; rowIndex++) {
            const row = gridPositions[rowIndex];
            for (let colIndex = 0; colIndex < row.length; colIndex++) {
                const position = row[colIndex];
                const newPosition = isColumn ? swapGridPosition(position) : position;
                if (newPosition.justifyContent === justifyContent && newPosition.alignItems === alignSelf) {
                    return [rowIndex, colIndex];
                }
            }
        }
        return [0, 0];
    };

    const getBackgroundColor = (isSelected: boolean) =>
        `hover:bg-blue-700 ${isSelected ? 'bg-blue-600' : 'hover:bg-opacity-50'}`;

    const getSelectedCondition = (
        d: string,
        isMixedAlignSelf: boolean,
        justifyContent: string | undefined,
        alignSelf: string | undefined
    ) =>
        (isMixedAlignSelf && justifyContent === 'space-between') !== true &&
        (d === justifyContent || (justifyContent === 'space-between' && d === alignSelf));

    const handleButtonClick = (d: GridPosition['alignItems']) => {
        if (justifyContent === 'space-between') {
            updateChildAlignments(d);
        } else {
            setJustifyContents(d);
        }
    };

    const renderVisibleButtons = () => {
        const index = getPosition();
        const fillHeight = () => {
            if (isMixedAlignSelf && justifyContent !== 'space-between') {
                return isColumn;
            } else {
                return !isColumn;
            }
        };

        let [width, height, padding] = [24, 24, 4];

        if (isMixedAlignSelf || justifyContent === 'space-between') {
            if (fillHeight()) {
                width = width * 3 + 4 * 2;
            } else {
                height = height * 3 + 4 * 2;
                index[0] = 0;
            }

            const simplifiedAlignArray: GridPosition['alignItems'][] = ['flex-start', 'center', 'flex-end'];

            return (
                <>
                    {simplifiedAlignArray.map((d, i) => (
                        <button
                            key={'long-button ' + d}
                            className={`absolute flex items-center justify-center rounded-lg ${getBackgroundColor(
                                getSelectedCondition(d, isMixedAlignSelf, justifyContent, alignSelf)
                            )}`}
                            style={{
                                left: fillHeight() ? 0 : width * i + padding * i,
                                top: fillHeight() ? height * i + padding * i : 0,
                                width: width,
                                height: height,
                            }}
                            onClick={() => handleButtonClick(d)}
                        >
                            {getSelectedCondition(d, isMixedAlignSelf, justifyContent, alignSelf) && (
                                <Check size={16} color="#ffffff" weight="bold" />
                            )}
                        </button>
                    ))}
                </>
            );
        }

        return (
            <div className="grid grid-cols-3 gap-1">
                {gridPositions.map((row, rowIndex) =>
                    row.map((position, colIndex) => {
                        const updatedPos = isColumn ? swapGridPosition(position) : position;
                        const isSelected =
                            updatedPos.justifyContent === justifyContent && updatedPos.alignItems === alignSelf;
                        return (
                            <button
                                key={`${rowIndex}-${colIndex}`}
                                className={`flex items-center justify-center rounded-lg ${getBackgroundColor(
                                    isSelected
                                )}`}
                                style={{
                                    width: width,
                                    height: height,
                                }}
                                onClick={() => handleGridItemClick(updatedPos)}
                            >
                                {isSelected && <Check size={16} color="#ffffff" weight="bold" />}
                            </button>
                        );
                    })
                )}
            </div>
        );
    };

    const renderDots = () => {
        return gridPositions.map((row, rowIndex) =>
            row.map((position, colIndex) => (
                <div key={`${rowIndex}-${colIndex}`} className="flex h-full w-full items-center justify-center">
                    <div className={`bg-neutral-200' h-1 w-1 rounded-full bg-neutral-200`} />
                </div>
            ))
        );
    };

    return (
        <div className="flex h-24 flex-row space-x-3">
            <div className="relative flex h-24 w-24 flex-shrink-0 items-center justify-center rounded-lg bg-black ring-1 ring-blue-500">
                <div className="grid h-20 w-20 grid-cols-3 gap-1">{renderDots()}</div>
                <div className="absolute inset-0 m-auto h-20 w-20">{renderVisibleButtons()}</div>
            </div>
            <div className="flex h-full w-full flex-col items-start">
                <button
                    className={`flex items-center justify-center gap-2 rounded p-2 text-xs text-white hover:bg-black`}
                    onClick={() => {
                        if (justifyContent === 'space-between') {
                            setJustifyContents('center');
                        } else {
                            setJustifyContents('space-between');
                        }
                    }}
                >
                    {justifyContent === 'space-between' ? (
                        <Circle size={14} weight="fill" color="#2590EB" />
                    ) : (
                        <Circle size={14} />
                    )}
                    Space-Between
                </button>
                <button
                    className={`flex items-center justify-center gap-2 rounded p-2 text-xs text-white hover:bg-black ${
                        isMixedAlignSelf ? 'visible' : 'pointer-events-none opacity-0'
                    }`}
                    onClick={() => updateChildAlignments('center')}
                >
                    <ArrowClockwise size={14} />
                    Realign Children
                </button>
            </div>
        </div>
    );
};

const options: Array<{value: string; icon: React.ReactNode}> = [
    {value: 'column', icon: <ArrowDown size={14} />},
    {value: 'row', icon: <ArrowRight size={14} />},
];

interface DirectionProps {}

export const DirectionSelector: React.FC<React.PropsWithChildren<DirectionProps>> = () => {
    const {selectedNode, updateSelectedNodes} = useViewContext();

    return (
        <div className="flex flex-row space-x-3 text-white">
            <TabbedButtonToggle
                value={
                    selectedNode?.calculatedLayoutAbstraction.flexDirection === 'Mixed'
                        ? undefined
                        : selectedNode?.calculatedLayoutAbstraction.flexDirection
                }
                items={options}
                onClick={() => {
                    updateSelectedNodes((node) => {
                        node.inputLayoutAbstraction.flexDirection =
                            node.calculatedLayoutAbstraction.flexDirection === 'column' ? 'row' : 'column';

                        const counts = node.children
                            .map((child) => child.calculatedLayoutAbstraction.alignSelf)
                            .reduce((prev, curr) => {
                                prev[curr] = (prev[curr] || 0) + 1;
                                return prev;
                            }, {});

                        const mostCommonChildAlign = (Object.keys(counts)
                            .sort((a, b) => counts[a] - counts[b])
                            .pop() || 'flex-start') as EnrichedAstNode['calculatedLayoutAbstraction']['alignSelf'];

                        node.inputLayoutAbstraction.justifyContent = mostCommonChildAlign;

                        node.children.forEach((child) => {
                            child.inputLayoutAbstraction.alignSelf =
                                node.calculatedLayoutAbstraction.justifyContent === 'space-between'
                                    ? 'center'
                                    : node.calculatedLayoutAbstraction.justifyContent;
                        });
                    });
                }}
            />
        </div>
    );
};
